/**
 * The motorcycle, driven (IDEAS X31; decision 125(d); R12 step 5, "a vehicle moves"). A bike is a `motorcycle` structure
 * — a vehicle is a structure units get into (§5.13) — with a `drive` block: its heading, its speed, its build (gas or
 * electric). Its rider is the unit in its driver seat (a parked body, R12 step 3), taken with the seat input (E).
 *
 * One call in the action tick's system order (`stepDrive`, after the seats): each bike moves on the surface walk's
 * tangent frame with momentum —
 *   - the rider's `forward` opens the throttle (toward the build's top speed, a few walks) or, rolling, brakes; from a
 *     stop it backs up slowly; let go, it coasts to a stop; nobody in its driver's seat, it brakes to a stop and holds (63.1d);
 *   - `right` and the mouse's `turn` steer it, the turn rate capped by speed (the turning radius grows with speed, and
 *     a bike standing still does not turn);
 *   - slopes slow it uphill and speed it downhill under the felt gravity, the gas engine climbing better;
 *   - it collides through today's walk obstacle path (`stepWalk`, behind `moveBike`: lane F's footprint step replaces
 *     that one call); blocked above `crashSpeed` it throws its rider (off the bike, hurt by the speed), below it stops;
 *   - the rider rides with it: on its spot, facing its heading.
 * While the throttle is open its noise is heard (`heard`, the bots' hearing) at `BIKE_DRIVE.noise` × the hearing range:
 * the gas bike loud, the electric one quiet — the stealth layer's insertion vehicle.
 *
 * Deterministic (dmath, no draws). Moving bikes are not walk obstacles (core/surfaceWalk.ts leaves them out, so the
 * planet's filed obstacles are not rebuilt every tick); bodies bumping bodies is lane F's footprint step.
 */
import type { GameState, PlanetState, PlayerState, StructureState, UnitIntent, Vec3 } from '../types';
import { VEHICLE_MODEL_SIZE, BIKE_DRIVE, DRIVE_DIALS, PERSON_BODY_HEIGHT, TANK } from '../constants';
import { personSetOf, type PersonSet } from './scaleProfile';
import { vecAdd, vecScale, vecSub, vecNormalize, vecLength } from '../math';
import { dcos, dsin } from './dmath';
import { stepWalk, projectToTangent, turnHeading } from './surfaceWalk';
import { footprintObstacles, vehicleMover, movingHullNear, personHip } from './hits';
import { personSurfaceRadius } from './fineTerrain';
import { getSurfaceRadius } from './surface';
import { damageUnit } from './damage';
import { dismountUnit, seatUnit } from './seats';
import { drives } from './body';
import { vehicleFrame } from './vehicleFrame';
import { SEAT_POINTS } from '../constants/hitboxes';
import { BODIES, type BodyKey } from '../constants/body';
import { strikePeople, type ImpactEvent } from './vehicleImpact';

export type BikeVariant = 'gas' | 'electric';
export interface DriveState { heading: Vec3; speed: number; variant: BikeVariant; throttle?: number; yawRate?: number }


/** 63.1d: a driving body's numbers — the bike's, or the transport's (the 6×6, sized for its hull and wheels). */
/** A driving body's numbers (DRIVE_DIALS' rows); 197 step 1: `pivotRate` (radians a second) for a body that turns on the spot (the tracked carrier's tracks). */
export type DriveSpec = typeof BIKE_DRIVE & { pivotRate?: number };
export function driveSpec(type: StructureState['type']): DriveSpec {
  // 63.1o (the coordinator: a body's own dials): each driving body's row — a new one was given the bike's (a crash speed that
  // threw its driver, no roll); the bike's stays only for a body that does not drive, which nothing asks
  return DRIVE_DIALS[type as keyof typeof DRIVE_DIALS] ?? BIKE_DRIVE;
}

/**
 * 63.1d step 2: where an exposed seat's occupant stands (its measured point, people in the vehicle's frame), or null for a
 * closed one. Exposed: a weapon's hatch (the transport's cupola) — the driver's is closed in a fight and the bays are
 * inside (ARCHITECTURE §5.13 Vehicle roles).
 */
/** How far an exposed occupant's eye clears its hull's top (people): it looks over it, and over it is seen. */
const EYE_CLEAR = 0.08;
/**
 * 63.1p (the owner: "if I park just peeking over a hill the bot won't resolve targets until I've put the whole vehicle
 * over"): a gunner standing in its hatch has its eye this far over the hatch's rim (people) — chest at the rim, the head
 * over it. Over the hull's top alone, the APC's cupola gunner looked from 1.66–1.70 people, a person under where the
 * hatch's gunner stands (the rim at 1.43, the eye about 1.9): hull down, the crest hid what the cupola sees.
 */
const EYE_OVER_RIM = 0.35;
/** A body's measured hull top, in people (its shape's highest part). */
function hullTop(type: StructureState['type']): number {
  let top = 0;
  for (const p of BODIES[type].hitbox.parts) top = Math.max(top, p.shape === 'box' ? p.centre[1] + p.half[1] : Math.max(p.a[1], p.b[1]) + p.radius);
  return top;
}

export function exposedSeatPoint(type: StructureState['type'], role: string, index: number): readonly [number, number, number] | null {
  if (role !== 'weapon') return null;
  return SEAT_POINTS[type as BodyKey]?.[index]?.at ?? null;
}

/**
 * 63.1d step 2 / 156 / 63.1p: how high the occupant of an exposed seat stands in the vehicle's frame (people), its eye
 * `eyePeople` over its feet — its measured point, raised so its eye clears the hull's top and, in a hatch ring (the
 * cupola's), the rim by a head; null for a closed seat. A pintle seat's gunner (the technical's, 63.1o's follow-up) stands
 * on its floor behind the post: its rim is the bed's rail, not a ring it stands in, and its eye is over the gun on its own.
 */
export function hatchStandPeople(type: StructureState['type'], role: string, index: number, eyePeople: number): number | null {
  const at = exposedSeatPoint(type, role, index);
  if (!at) return null;
  const seat = (VEHICLE_MODEL_SIZE as Record<string, { seats?: ReadonlyArray<{ rim?: number; pintle?: number }> } | undefined>)[type]?.seats?.[index];
  // standing in the hatch with its eye over the hull's top (the gun's cradle is at the gunner's eye: looking over it)
  const ring = seat?.rim !== undefined && seat.pintle === undefined ? seat.rim + EYE_OVER_RIM - eyePeople : -Infinity;
  return Math.max(at[1], hullTop(type) - eyePeople + EYE_CLEAR, ring);
}

/**
 * 63.1d step 2 / 156: where the occupant of an exposed seat (the cupola's) stands — its measured point in the vehicle's
 * frame on the ground, raised so its eye clears the hull's top (hatchStandPeople); null for a closed seat or a body that
 * does not drive.
 */
export function hatchSeatPos(planet: PlanetState, s: StructureState, index: number, person: number, ps: PersonSet): Vec3 | null {
  const seat = s.seats?.[index];
  if (!seat || !drives(s)) return null;
  const at = exposedSeatPoint(s.type, seat.role, index);
  if (!at) return null;
  const frame = vehicleFrame(planet, s, person, ps);
  const stand = hatchStandPeople(s.type, seat.role, index, ps.losEyeHeight / person)!;
  return vecAdd(frame.foot, vecAdd(vecScale(frame.left, at[0] * person), vecAdd(vecScale(frame.up, stand * person), vecScale(frame.front, at[2] * person))));
}

/** The unit in a bike's driver seat, or null. */
export function riderOf(s: Pick<StructureState, 'seats'>): number | null {
  const seat = s.seats?.[0];
  return seat ? seatUnit(seat) : null;
}

/**
 * The fastest a bike turns at `speed` (radians a second): speed over the turning radius, which grows with speed. 197 step 1:
 * a body with a `pivotRate` (the tracked carrier: its tracks counter-rotate) turns at least that fast, standing still too.
 */
export function maxYawRate(speed: number, person: number, spec: DriveSpec = BIKE_DRIVE): number {
  const v = Math.abs(speed), r = (spec.turnRadius.min + spec.turnRadius.perSpeed * (v / person)) * person;
  return Math.max(r > 0 ? v / r : 0, spec.pivotRate ?? 0);
}

/** A bike's top speed and acceleration for its build, in world units (from the person's walk). */
export function bikeNumbers(variant: BikeVariant, walk: number, spec: DriveSpec = BIKE_DRIVE): { top: number; accel: number; climb: number } {
  const b = spec[variant];
  return { top: b.top * walk, accel: b.accel * walk, climb: b.climb };
}

/** The one collision call (lane F's footprint step replaces it): move a bike `dist` along its heading on the walk path. */
function moveBike(state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets' | 'config'>, s: StructureState, heading: Vec3, dist: number, dt: number, spec: DriveSpec = BIKE_DRIVE): { surfaceDir: Vec3; blocked: boolean } {
  const planet = state.planets[s.planetIndex];
  const ps = personSetOf(state as Pick<GameState, 'config'>);
  const radius = spec.radius * PERSON_BODY_HEIGHT * ps.k;
  const size = VEHICLE_MODEL_SIZE[s.type as keyof typeof VEHICLE_MODEL_SIZE] ?? VEHICLE_MODEL_SIZE.motorcycle;
  // 63.3 step 4: the bike drives against the shapes' footprints for its own hull — over what is lower than 35 % of it
  const filed = footprintObstacles(state, s.planetIndex, vehicleMover(radius, size.height * PERSON_BODY_HEIGHT * ps.k));
  const step = stepWalk(s.surfaceDir, heading, planet, filed.list, dist >= 0 ? 1 : -1, 0, Math.abs(dist) / dt, dt,
    { unitRadius: radius, maxSlope: ps.maxSlope * spec.slopeReach, fineAmplitude: ps.fineAmplitude, fineWavelength: ps.fineWavelength, k: ps.k }, filed);
  // 63.1d step 2: nor into another vehicle's hull (where it stands this tick) — tested at its own front, half a wheelbase ahead
  if (!step.blocked && step.moved > 0) {
    const sgn = dist >= 0 ? 1 : -1, n = vecNormalize(step.surfaceDir), a = (sgn * spec.wheelbase * 0.5 * PERSON_BODY_HEIGHT * ps.k) / planet.radius;
    const nose = vecNormalize(vecAdd(vecScale(n, dcos(a)), vecScale(vecNormalize(projectToTangent(heading, n)), dsin(a))));
    if (movingHullNear(personHip(planet, nose, ps), radius, s.planetIndex, state, s.id)) return { surfaceDir: s.surfaceDir, blocked: true };
  }
  return { surfaceDir: step.surfaceDir, blocked: step.blocked };
}

/**
 * 63.1m (fix/63.1m-bike-flicker): would the body be blocked moving `dist` (world units; + ahead, − back) along `heading`
 * from where it stands now — moveBike's own test (the shapes' footprints, the slope, another vehicle's hull), for a
 * rider's look-ahead. Pure.
 */
export function blockedAlong(state: Pick<GameState, 'rocks' | 'flora' | 'structures' | 'planets' | 'config'>, s: StructureState, heading: Vec3, dist: number): boolean {
  return moveBike(state, s, heading, dist, 1, driveSpec(s.type)).blocked;
}

export type DriveEvent = { kind: 'bikeCrash'; structureId: number; unitId: number; speed: number; damage: number } | ImpactEvent;   // (63.19: `struck`, and a bike's rider thrown by its blow)

/**
 * One tick of every bike (after the seats, before the units act). `intents` is indexed like `players`. Returns null
 * when no bike moved or changed. `tick`: the action tick (63.19's once-a-second pair rule; with GameConfig.vehicleImpact).
 */
export function stepDrive(state: GameState, intents: readonly UnitIntent[], dt: number, tick = 0): { state: GameState; events: DriveEvent[]; heard: Array<{ unitId: number; pos: Vec3; loud: number }> } | null {
  if (!state.structures.some(drives)) return null;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  let s0 = state;
  let structures: StructureState[] | null = null;
  const events: DriveEvent[] = [], heard: Array<{ unitId: number; pos: Vec3; loud: number }> = [];
  for (let i = 0; i < state.structures.length; i++) {
    const s = state.structures[i];
    if (!drives(s) || s.health <= 0) continue;
    const d = s.drive!, planet = state.planets[s.planetIndex];
    const spec = driveSpec(s.type);   // 63.1d: the bike's numbers, or the transport's
    const riderId = riderOf(s);
    const ri = riderId === null ? -1 : state.players.findIndex(p => p.id === riderId);
    const intent = ri >= 0 && !s.disabled ? intents[ri] : undefined;   // (69.29 step 4: a mobility kill — no throttle, no steering: it brakes to a stop)
    const n = vecNormalize(s.surfaceDir);
    let heading = vecNormalize(projectToTangent(d.heading, n));
    const { top, accel, climb } = bikeNumbers(d.variant, ps.walkSpeed, spec);
    // steering: the mouse's turn and the bars (right), capped by the speed's turn rate
    const cap = maxYawRate(d.speed, person, spec) * dt;
    const want = intent ? intent.turn + intent.right * maxYawRate(Math.max(Math.abs(d.speed), 0.5 * top), person, spec) * dt : 0;
    const yaw = Math.max(-cap, Math.min(cap, want));
    if (yaw !== 0) heading = vecNormalize(turnHeading(heading, n, yaw));   // + = toward the rider's right, as the walk turns
    // speed: throttle toward top, braking against the roll, backing up slowly from a stop; coasting with the throttle shut; braked to a stop with no rider
    const throttle = intent ? Math.max(-1, Math.min(1, intent.forward)) : 0;
    // the hill under it, over the wheelbase (a probe each way, so the terrain's facets don't jolt it). Its pull is in the
    // walk's units, not the felt gravity: at the people's scale felt gravity is a hundred times the bike's own
    // acceleration (it keeps the jumps' times), so a real g would stall it on a facet. Uphill slows it and lowers the
    // speed an open throttle holds; downhill speeds it; the engine carries `climb` of the pull (gas more than electric).
    const ground = (dir: Vec3) => (ps.fineAmplitude > 0 ? personSurfaceRadius(planet, dir, ps) : getSurfaceRadius(planet, dir));
    const probe = spec.wheelbase * 0.5 * person, a = probe / planet.radius;
    const along = (sgn: number) => vecNormalize(vecAdd(vecScale(n, dcos(a)), vecScale(heading, sgn * dsin(a))));
    const grade = Math.max(-1, Math.min(1, (ground(along(1)) - ground(along(-1))) / (2 * probe)));
    const pull = spec.hill * ps.walkSpeed * grade;
    let v = d.speed;
    if (throttle > 0) {
      // under power: toward the speed this build holds on this grade, the pull mostly carried
      const holds = top * Math.max(spec.hillTop.min, Math.min(spec.hillTop.max, 1 - grade * (1 - climb) * spec.hillTop.per));
      if (v < 0) v = Math.min(0, v + spec.brake * ps.walkSpeed * dt);
      else if (v < holds) v = Math.min(holds, v + (accel * throttle - pull * (1 - climb)) * dt);
      else v = Math.max(holds, v - (spec.drag * ps.walkSpeed + Math.max(0, pull)) * dt);
    } else if (throttle < 0) {
      v = v > 0 ? Math.max(0, v - spec.brake * ps.walkSpeed * dt) : Math.max(-spec.reverse * ps.walkSpeed, v + accel * 0.5 * throttle * dt);
    } else if (!intent) {
      // 63.1d (lane B's departures trace: parked APCs rolling off, a lone gunner aboard): nobody in the driver's seat,
      // nothing drives it — it brakes to a stop and holds, the hill's pull with it (the hand brake). Before, a body left
      // moving (its driver out, or moved to another seat) coasted, and downhill the pull outran the drag
      v = v > 0 ? Math.max(0, v - spec.brake * ps.walkSpeed * dt) : Math.min(0, v + spec.brake * ps.walkSpeed * dt);
    } else {
      // coasting: the drag and the hill; stopped with the throttle shut it holds (the brake, or the stand) — a parked bike never rolls away
      v -= pull * dt;
      v = v > 0 ? Math.max(0, v - spec.drag * ps.walkSpeed * dt) : Math.min(0, v + spec.drag * ps.walkSpeed * dt);
      if (Math.abs(d.speed) <= spec.holdSpeed * ps.walkSpeed) v = 0;
    }
    // move (the walk path's collision); blocked above the crash speed throws the rider
    let surfaceDir = n, crashed = false;
    if (Math.abs(v) > 1e-9) {
      const m = moveBike(s0, s, heading, v * dt, dt, spec);
      surfaceDir = m.surfaceDir;
      if (m.blocked) { crashed = Math.abs(v) > spec.crashSpeed * ps.walkSpeed; v = 0; }
    }
    const next: StructureState = { ...s, surfaceDir, drive: { ...d, heading, speed: v, throttle, yawRate: yaw / dt } };
    (structures ??= s0.structures.slice())[i] = next;
    s0 = { ...s0, structures };
    // the rider rides with it — or is thrown
    if (ri >= 0) {
      const rider = s0.players[ri];
      if (crashed) {
        const speed = Math.abs(d.speed);
        const damage = spec.crashDamage * (speed / ps.walkSpeed) * (speed / ps.walkSpeed);
        s0 = dismountUnit(s0, rider.id, 0);
        const hit = damageUnit(s0.players[ri], damage, 'landing');
        s0 = { ...s0, players: s0.players.map((p, k) => (k === ri ? hit.unit : p)) };
        structures = s0.structures.slice();
        events.push({ kind: 'bikeCrash', structureId: s.id, unitId: rider.id, speed, damage });
      } else if (throttle > 0) heard.push({ unitId: rider.id, pos: vecAdd(planet.position, vecScale(surfaceDir, ground(surfaceDir) + person * 0.5)), loud: spec.noise[d.variant] });
    }
    // 63.1d: everyone aboard rides with it, facing its way (a bike's rider alone; a transport's driver, gunner and bays) —
    // unless thrown above. Step 2: an exposed hatch's occupant (the cupola gunner) stands in its hatch, on the hull's seat on
    // the ground (core/vehicleFrame.ts), its feet at the seat's measured point
    const aboard = new Map<number, number>();
    (s0.structures[i].seats ?? []).forEach((seat, k) => { const u = seatUnit(seat); if (u !== null) aboard.set(u, k); });
    if (aboard.size) {
      const moved = s0.structures[i];
      let frame: ReturnType<typeof vehicleFrame> | null = null;
      s0 = { ...s0, players: s0.players.map(p => {
        const k = aboard.get(p.id);
        if (k === undefined) return p;
        const at = exposedSeatPoint(moved.type, moved.seats![k].role, k);
        // 69.29: a tank's turret gunner, closed in its turret, keeps its own bearing (the gun's: stabilised), laid on the ground where the hull now stands
        if (!at && moved.type === 'tank' && k === TANK.turretSeat) { const n1 = vecNormalize(surfaceDir), own = p.heading ? vecNormalize(projectToTangent(p.heading, n1)) : heading; return { ...p, surfaceDir, heading: own, facing: own } as PlayerState; }
        if (!at) { if (p.seatPos === undefined) return { ...p, surfaceDir, heading, facing: heading } as PlayerState; const { seatPos: _s, ...rest } = p; return { ...rest, surfaceDir, heading, facing: heading } as PlayerState; }
        const seatPos = hatchSeatPos(planet, moved, k, person, ps)!;
        // step 3: the gunner keeps its own bearing (the gun's), laid on the ground where the hull now stands
        const n1 = vecNormalize(surfaceDir), own = p.heading ? vecNormalize(projectToTangent(p.heading, n1)) : heading;
        return { ...p, surfaceDir, heading: own, facing: own, seatPos } as PlayerState;
      }) };
    }
    // 63.19 (GameConfig.vehicleImpact; core/vehicleImpact.ts): the hull, moved, strikes the people on foot it meets
    if (s0.config.vehicleImpact) {
      const struck = strikePeople(s0, i, riderOf(s0.structures[i]), tick);
      if (struck) { s0 = struck.state; structures = s0.structures; events.push(...struck.events); }
    }
  }
  return structures ? { state: s0, events, heard } : null;
}

/** The world direction a bike faces, flattened to its ground (for the view and the bots). */
export function bikeHeading(s: Pick<StructureState, 'surfaceDir' | 'drive'>): Vec3 {
  const n = vecNormalize(s.surfaceDir);
  return vecNormalize(projectToTangent(s.drive?.heading ?? { x: 0, y: 1, z: 0 }, n));
}

/** How far apart along the ground two directions are (world units). */
export function groundGap(a: Vec3, b: Vec3, radius: number): number {
  return vecLength(vecSub(vecNormalize(a), vecNormalize(b))) * radius;
}
